import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { blueGray30 } from 'apps/new-web/views/themes/colors';
import Icon from 'apps/new-web/views/components/icon';
import CLOSE_CIRCLE_SOLID from '@ifit/react-icons/dist/icons/CLOSE_CIRCLE_SOLID';
import I18n from 'apps/new-web/views/components/i18n';
import useOnClickOutside from './useOnClickOutside';

const GenericModal = ({
  children,
  closeX,
  closeXText,
  handleModalClose,
  isOpen,
  title,
  containerClass,
}) => {
  useEffect(() => {
    window.addEventListener('keydown', handleEscTabPress, false);
    window.addEventListener('keydown', prepareHandleClose);
    return () => {
      document.removeEventListener('keydown', handleEscTabPress, false);
    };
  }, []);

  const ref = useRef();
  useOnClickOutside(ref, () => (typeof handleModalClose === 'function') && handleModalClose());

  useEffect(() => {
    const modal = document.querySelector('#generic-modal');
    if (isOpen) {
      trapFocus(modal);
    } else {
      releaseFocus();
    }

    window.addEventListener('keydown', prepareHandleClose);

    return () => window.removeEventListener('keydown', prepareHandleClose);
  }, [isOpen]);

  const FOCUS_ITEMS = `
    a, button, input, select, textarea, svg, area, details, summary,
    iframe, object, embed,
    [tabindex], [contenteditable]
  `;

  function trapFocus(focusNode, rootNode = document) {
    const nodes = [...rootNode.querySelectorAll(FOCUS_ITEMS)]
      .filter(node => !focusNode.contains(node) && node.getAttribute('tabindex') !== '-1');
    nodes.forEach((node) => {
      node.setAttribute('tabindex', '-1');
      node.setAttribute('data-trap', true);
    });
  }

  function releaseFocus(rootNode = document) {
    const nodes = rootNode.querySelectorAll('[data-trap=true]');
    if (!nodes) return;
    nodes.forEach((node) => {
      node.removeAttribute('tabindex');
      node.removeAttribute('data-trap');
    });
  }

  function setElementFocus(ev) {
    const modal = document.querySelector('#generic-modal');
    const focusableElements = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
    const firstFocusableElement = modal.querySelectorAll(focusableElements)[0];
    const focusableContent = modal.querySelectorAll(focusableElements);
    const lastFocusableElement = focusableContent[focusableContent.length - 1];
    if (ev.shiftKey) {
      if (document.activeElement === firstFocusableElement) {
        lastFocusableElement.focus();
        ev.preventDefault();
      }
    } else {
      if (document.activeElement === lastFocusableElement) {
        firstFocusableElement.focus();
        ev.preventDefault();
      }
    }
  }

  function prepareHandleClose({ target, keyCode }) {
    let triedToCloseWithClick = false;
    let triedToCloseWithKeyboard = false;

    if (keyCode) {
      triedToCloseWithKeyboard = keyCode === 27;
    } else if (target) {
      const closeableClasses = [
        'close-icon',
        'close-icon-wrapper',
        'workout-modal-cover'
      ];
      triedToCloseWithClick = closeableClasses.some(klass =>
        target.classList.contains(klass)
      );
    }

    if (triedToCloseWithClick || triedToCloseWithKeyboard) {
      setTimeout(() => handleModalClose(event), 300);
    }
  }

  const handleEscTabPress = (ev) => {
    if (ev.key === 'Esc' || ev.keyCode === 27) {
      handleModalClose();
    } else if (ev.key === 'Tab' || ev.keyCode === 9) {
      setElementFocus(ev);
    }
  };

  return (
    <div
      id="generic-modal"
      className={ classNames('generic-modal', { 'open': isOpen }) }
      role="dialog"
      aria-modal={ isOpen }
      aria-labelledby={ title }
      aria-hidden={ !isOpen }
    >
      <div className={ classNames('modal-container', containerClass) }>
        { closeX &&
        <button className="close-button" onClick={ handleModalClose }>
          <Icon
            icon={ CLOSE_CIRCLE_SOLID }
            color={ blueGray30 }
            size={ 30 }
          />
          <span className="sr-only"><I18n>{ closeXText }</I18n></span>
        </button>
        }
        <div className="modal-inner" ref={ ref }>
          { children }
        </div>
      </div>
    </div>
  );
};

GenericModal.propTypes = {
  children: PropTypes.node.isRequired,
  closeX: PropTypes.bool,
  content: PropTypes.string,
  handleModalClose: PropTypes.func,
  isOpen: PropTypes.bool,
  primaryActionLabel: PropTypes.string,
  title: PropTypes.string.isRequired,
  containerClass: PropTypes.string,
  closeXText: PropTypes.string
};

GenericModal.defaultProps = {
  containerClass: '',
  closeXText: 'Close Modal'
};

export default GenericModal;
